import { API_URL } from '@/config';
import React, { useState, useEffect } from 'react';
import { Image as ImageIcon, Video, X, Upload, Play } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { useOutletContext } from 'react-router-dom';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

const GalleryPage = () => {
  const { user } = useOutletContext();
  const [media, setMedia] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ event_id: 'all', type: 'all' });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
  const [uploadData, setUploadData] = useState({ url: '', caption: '', event_id: '', type: 'photo' });
  const [selectedMedia, setSelectedMedia] = useState(null); // For video/image preview

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.event_id && filters.event_id !== 'all') params.append('event_id', filters.event_id);
      if (filters.type && filters.type !== 'all') params.append('type', filters.type);
      params.append('status', 'approved');
      
      const [mediaRes, eventsRes] = await Promise.all([
        axios.get(`${API}/media?${params.toString()}`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } }),
        axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } })
      ]);
      
      setMedia(mediaRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Error loading gallery:', error);
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.url) {
      toast.error('Please upload a file or enter a URL');
      return;
    }
    setUploading(true);
    try {
      await axios.post(`${API}/media`, uploadData, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Media uploaded! Awaiting approval.');
      setUploadOpen(false);
      setUploadData({ url: '', caption: '', event_id: '', type: 'photo' });
      setUploadMode('file');
      loadData();
    } catch (error) {
      toast.error('Failed to upload media');
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      toast.error('Please select an image or video file');
      return;
    }
    
    // Auto-set type based on file
    const mediaType = isImage ? 'photo' : 'video';
    
    setFileUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadData(prev => ({ ...prev, url: response.data.url, type: mediaType }));
      toast.success('File uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setFileUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="gallery-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#0F172A' }}>Gallery</h1>
          <p className="text-muted-foreground">Browse our moments</p>
        </div>
        
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogTrigger asChild>
            <Button data-testid="upload-btn">Upload Media</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Photo or Video</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              {/* Upload Mode Toggle */}
              <div className="flex gap-2 p-1 bg-muted rounded-lg">
                <button
                  type="button"
                  onClick={() => setUploadMode('file')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    uploadMode === 'file' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  Upload File
                </button>
                <button
                  type="button"
                  onClick={() => setUploadMode('url')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    uploadMode === 'url' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  Enter URL
                </button>
              </div>

              {/* File Upload */}
              {uploadMode === 'file' && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Select File</label>
                  {uploadData.url ? (
                    <div className="relative">
                      {uploadData.type === 'photo' ? (
                        <img 
                          src={getImageUrl(uploadData.url)} 
                          alt="Preview" 
                          className="w-full h-48 object-contain rounded-lg bg-muted"
                        />
                      ) : (
                        <video 
                          src={getImageUrl(uploadData.url)} 
                          className="w-full h-48 object-contain rounded-lg bg-muted"
                          controls
                        />
                      )}
                      <Button 
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="absolute top-2 right-2"
                        onClick={() => setUploadData(prev => ({ ...prev, url: '' }))}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                      {fileUploading ? (
                        <div className="flex flex-col items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2"></div>
                          <span className="text-sm text-muted-foreground">Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <ImageIcon className="w-8 h-8 mb-2 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Click to upload image or video</span>
                          <span className="text-xs text-muted-foreground mt-1">JPG, PNG, GIF, MP4, MOV</span>
                        </>
                      )}
                      <input 
                        type="file" 
                        accept="image/*,video/*" 
                        className="hidden" 
                        onChange={handleFileUpload}
                        disabled={fileUploading}
                      />
                    </label>
                  )}
                </div>
              )}

              {/* URL Input */}
              {uploadMode === 'url' && (
                <>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Type</label>
                    <Select value={uploadData.type} onValueChange={(v) => setUploadData({...uploadData, type: v})}>
                      <SelectTrigger data-testid="type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="photo">Photo</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Media URL</label>
                    <Input
                      placeholder="https://example.com/image.jpg"
                      value={uploadData.url}
                      onChange={(e) => setUploadData({...uploadData, url: e.target.value})}
                      data-testid="url-input"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="text-sm font-medium mb-2 block">Caption (optional)</label>
                <Input
                  placeholder="Add a caption..."
                  value={uploadData.caption}
                  onChange={(e) => setUploadData({...uploadData, caption: e.target.value})}
                  data-testid="caption-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Link to Event (optional)</label>
                <Select value={uploadData.event_id} onValueChange={(v) => setUploadData({...uploadData, event_id: v})}>
                  <SelectTrigger data-testid="event-select">
                    <SelectValue placeholder="Select event" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No event</SelectItem>
                    {events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={uploading || fileUploading || !uploadData.url} 
                data-testid="upload-submit-btn"
              >
                {uploading ? 'Submitting...' : 'Submit for Approval'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="flex gap-4">
        <Select value={filters.event_id} onValueChange={(v) => setFilters({...filters, event_id: v})}>
          <SelectTrigger className="w-48" data-testid="filter-event">
            <SelectValue placeholder="All Events" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            {events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}
          </SelectContent>
        </Select>
        
        <Select value={filters.type} onValueChange={(v) => setFilters({...filters, type: v})}>
          <SelectTrigger className="w-48" data-testid="filter-type">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="photo">Photos</SelectItem>
            <SelectItem value="video">Videos</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {media.length === 0 ? (
        <Card data-testid="no-media">
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">No media found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
          {media.map((item) => (
            <div 
              key={item.media_id} 
              className="aspect-square rounded-lg overflow-hidden hover-lift relative group cursor-pointer"
              onClick={() => setSelectedMedia(item)}
            >
              {item.type === 'photo' ? (
                <img src={getImageUrl(item.url)} alt={item.caption || 'Media'} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-royal flex items-center justify-center relative">
                  {/* Video thumbnail or placeholder */}
                  <video 
                    src={getImageUrl(item.url)} 
                    className="w-full h-full object-cover absolute inset-0"
                    muted
                    preload="metadata"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                      <Play className="w-8 h-8 text-primary ml-1" fill="currentColor" />
                    </div>
                  </div>
                </div>
              )}
              {item.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-2 text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  {item.caption}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Media Preview Dialog */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{selectedMedia?.caption || 'Media Preview'}</DialogTitle>
          </DialogHeader>
          {selectedMedia && (
            <div className="space-y-4">
              {selectedMedia.type === 'photo' ? (
                <img 
                  src={getImageUrl(selectedMedia.url)}
                  alt={selectedMedia.caption || 'Media'} 
                  className="w-full rounded-lg max-h-[70vh] object-contain" 
                />
              ) : (
                <video 
                  src={getImageUrl(selectedMedia.url)}
                  controls
                  autoPlay
                  className="w-full rounded-lg max-h-[70vh]"
                >
                  Your browser does not support the video tag.
                </video>
              )}
              {selectedMedia.caption && (
                <p className="text-muted-foreground">{selectedMedia.caption}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
